import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  exceptionsApi,
  scanningApi,
  tripsApi,
  type AssignmentPayload,
  type ExceptionRecord,
  type ScanEvent,
  type Trip,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { ConfirmModal, Modal } from '@/components/Modal';
import { AssignmentModal } from '@/components/AssignmentModal';
import { internalNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { humanize, stopTypeLabel } from '@/lib/format';

const BILLABILITY_LABEL: Record<string, string> = {
  billable: 'Billable',
  non_billable: 'Non-billable',
  case_by_case: 'Case-by-case',
};

function ageInDays(dateStr?: string): number {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
}

type ApprovalTab = 'trips' | 'exceptions' | 'rtb' | 'scans';

export function ApprovalQueue() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ApprovalTab>('trips');
  const toast = useToast();
  const [declineId, setDeclineId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declineError, setDeclineError] = useState('');
  const [assignTripId, setAssignTripId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rtbApprovingId, setRtbApprovingId] = useState<string | null>(null);
  const [rtbError, setRtbError] = useState('');
  const [scanResolvingId, setScanResolvingId] = useState<string | null>(null);
  const [scanError, setScanError] = useState('');

  const { data: trips, loading: listLoading, error: tripsError, reload } = useAsync(
    () => tripsApi.list({ filter: 'awaiting_approval' }),
    [],
  );

  const { data: pendingExceptions, loading: exceptionsLoading, error: exceptionsError, reload: reloadExceptions } = useAsync(
    () => exceptionsApi.approvalQueue(),
    [],
  );

  const { data: rtbTrips, loading: rtbLoading, error: rtbLoadError, reload: reloadRtb } = useAsync(
    () => tripsApi.list({ filter: 'rtb_pending' }),
    [],
  );

  const { data: pendingScans, loading: scansLoading, error: scansLoadError, reload: reloadScans } = useAsync(
    () => scanningApi.pendingQueue(),
    [],
  );

  const tripLabel = (tripId: string | null) => {
    const t = (trips ?? []).find((row) => row.id === tripId);
    return t?.tripNumber ?? t?.reference ?? 'Trip';
  };
  const assignTrip = (trips ?? []).find((row) => row.id === assignTripId) ?? null;
  const declineTrip = (trips ?? []).find((row) => row.id === declineId) ?? null;

  const openDecline = (tripId: string) => {
    setDeclineId(tripId);
    setDeclineReason('');
    setDeclineError('');
  };

  const handleApprove = async (assignment: AssignmentPayload) => {
    if (!assignTripId) return;
    setLoading(true);
    setAssignError('');
    try {
      await tripsApi.approve(assignTripId, assignment);
      toast(`${tripLabel(assignTripId)} assigned and approved`);
      setAssignTripId(null);
      reload();
    } catch (err) {
      setAssignError((err as { message?: string }).message ?? 'Failed to assign & approve');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineId) return;
    setLoading(true);
    setDeclineError('');
    try {
      await tripsApi.decline(declineId, declineReason.trim() || undefined);
      toast(`${tripLabel(declineId)} declined`);
      setDeclineId(null);
      reload();
    } catch (err) {
      setDeclineError((err as { message?: string }).message ?? 'Failed to decline trip');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRtb = async (tripId: string) => {
    setRtbApprovingId(tripId);
    setRtbError('');
    try {
      await tripsApi.approveRtb(tripId);
      toast('Return-to-base approved');
      await reloadRtb();
    } catch (err) {
      setRtbError((err as { message?: string }).message ?? 'Failed to approve return-to-base');
    } finally {
      setRtbApprovingId(null);
    }
  };

  const handleResolveScan = async (scanId: string, decision: 'accept' | 'reject') => {
    setScanResolvingId(scanId);
    setScanError('');
    try {
      await scanningApi.resolve(scanId, decision);
      await reloadScans();
    } catch (err) {
      setScanError((err as { message?: string }).message ?? 'Failed to resolve scan');
    } finally {
      setScanResolvingId(null);
    }
  };

  const refreshCurrent = () => {
    if (tab === 'trips') reload();
    else if (tab === 'exceptions') reloadExceptions();
    else if (tab === 'rtb') reloadRtb();
    else reloadScans();
  };

  const columns = [
    { key: 'reference', header: 'Reference' },
    { key: 'clientName', header: 'Client' },
    { key: 'origin', header: 'Origin' },
    { key: 'destination', header: 'Destination' },
    { key: 'scheduledDate', header: 'Date', render: (row: Trip) => row.scheduledDate ? new Date(row.scheduledDate).toLocaleDateString('en-PH') : '—' },
    {
      key: 'status',
      header: 'Status',
      render: (row: Trip) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'actions',
      header: '',
      render: (row: Trip) => (
        <div className="row" style={{ gap: 'var(--space-sm)' }}>
          <Button type="button" size="sm" onClick={(e) => { e.stopPropagation(); setAssignError(''); setAssignTripId(row.id); }}>Assign & approve</Button>
          <Button type="button" size="sm" variant="danger" onClick={(e) => { e.stopPropagation(); openDecline(row.id); }}>Decline</Button>
        </div>
      ),
    },
  ];

  const rtbColumns = [
    { key: 'reference', header: 'Reference' },
    { key: 'clientName', header: 'Client' },
    {
      key: 'status',
      header: 'Trip status',
      render: (row: Trip) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'rtbRequestedAt',
      header: 'Requested',
      render: (row: Trip) =>
        row.rtbRequestedAt ? new Date(row.rtbRequestedAt).toLocaleString('en-PH') : '—',
    },
    {
      key: 'actions',
      header: '',
      render: (row: Trip) => (
        <Button
          size="sm"
          loading={rtbApprovingId === row.id}
          onClick={(e) => {
            e.stopPropagation();
            void handleApproveRtb(row.id);
          }}
        >
          Approve return-to-base
        </Button>
      ),
    },
  ];

  const scanColumns = [
    {
      key: 'tripNumber',
      header: 'Trip',
      render: (row: ScanEvent) => row.tripNumber ?? '—',
    },
    {
      key: 'stop',
      header: 'Stop',
      render: (row: ScanEvent) =>
        `Stop ${row.stopSequence ?? '—'} (${row.stopType ? stopTypeLabel(row.stopType) : '—'})`,
    },
    {
      key: 'barcode',
      header: 'Barcode',
      render: (row: ScanEvent) => <code>{row.barcode}</code>,
    },
    {
      key: 'scannedAt',
      header: 'Scanned',
      render: (row: ScanEvent) =>
        row.scannedAt ? new Date(row.scannedAt).toLocaleString('en-PH') : '—',
    },
    {
      key: 'actions',
      header: '',
      render: (row: ScanEvent) => (
        <div className="row" style={{ gap: 'var(--space-sm)' }}>
          <Button
            size="sm"
            loading={scanResolvingId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              void handleResolveScan(row.id, 'accept');
            }}
          >
            Accept
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={scanResolvingId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              void handleResolveScan(row.id, 'reject');
            }}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Layout navItems={internalNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Approval queue</h1>
            <p className="text-muted">Trips, exceptions, return-to-base, and scan variances awaiting decision</p>
          </div>
          <Button type="button" variant="secondary" onClick={refreshCurrent}>
            Refresh
          </Button>
        </div>

        <div className="approval-tabs">
          <button
            type="button"
            className={`approval-tab ${tab === 'trips' ? 'active' : ''}`}
            onClick={() => setTab('trips')}
          >
            Trips{(trips ?? []).length > 0 ? ` (${(trips ?? []).length})` : ''}
          </button>
          <button
            type="button"
            className={`approval-tab ${tab === 'exceptions' ? 'active' : ''}`}
            onClick={() => setTab('exceptions')}
          >
            Exceptions{(pendingExceptions ?? []).length > 0 ? ` (${(pendingExceptions ?? []).length})` : ''}
          </button>
          <button
            type="button"
            className={`approval-tab ${tab === 'rtb' ? 'active' : ''}`}
            onClick={() => setTab('rtb')}
          >
            Return to base{(rtbTrips ?? []).length > 0 ? ` (${(rtbTrips ?? []).length})` : ''}
          </button>
          <button
            type="button"
            className={`approval-tab ${tab === 'scans' ? 'active' : ''}`}
            onClick={() => setTab('scans')}
          >
            Scan variances{(pendingScans ?? []).length > 0 ? ` (${(pendingScans ?? []).length})` : ''}
          </button>
        </div>

        {((tab === 'trips' && tripsError) || (tab === 'rtb' && rtbLoadError) || (tab === 'scans' && scansLoadError)) && (
          <div className="row-between form-error" role="alert">
            <span>Could not load this list — it may not be empty.</span>
            <Button type="button" size="sm" variant="secondary" onClick={refreshCurrent}>Retry</Button>
          </div>
        )}

        {tab === 'trips' ? (
          <DataTable
            columns={columns}
            data={trips ?? []}
            keyField="id"
            loading={listLoading}
            loadError={tripsError}
            dense
            onRowClick={(row) => navigate(`/app/dispatch/trips/${row.id}`)}
            emptyMessage="No trips awaiting approval. New portal bookings and sales submissions will appear here."
          />
        ) : tab === 'exceptions' ? (
          <ExceptionApprovalTab
            exceptions={pendingExceptions ?? []}
            loading={exceptionsLoading}
            loadError={exceptionsError}
            onRetry={reloadExceptions}
            onDecided={reloadExceptions}
          />
        ) : tab === 'rtb' ? (
          <>
            {rtbError && <div className="form-error">{rtbError}</div>}
            <DataTable
              columns={rtbColumns}
              data={rtbTrips ?? []}
              keyField="id"
              loading={rtbLoading}
              loadError={rtbLoadError}
              dense
              onRowClick={(row) => navigate(`/app/dispatch/trips/${row.id}`)}
              emptyMessage="No return-to-base requests pending."
            />
          </>
        ) : (
          <>
            {scanError && <div className="form-error">{scanError}</div>}
            <DataTable
              columns={scanColumns}
              data={pendingScans ?? []}
              keyField="id"
              loading={scansLoading}
              loadError={scansLoadError}
              dense
              onRowClick={(row) => row.tripId && navigate(`/app/dispatch/trips/${row.tripId}`)}
              emptyMessage="No pending scan variances."
            />
          </>
        )}
      </div>

      <ConfirmModal
        open={declineId !== null}
        onClose={() => setDeclineId(null)}
        onConfirm={handleDecline}
        title={`Decline ${declineTrip?.tripNumber ?? declineTrip?.reference ?? 'trip'}?`}
        message={
          <>
            <p style={{ marginBottom: 'var(--space-md)' }}>
              {declineTrip?.clientName ? `${declineTrip.clientName} will be notified` : 'The client will be notified'} that
              this booking was declined. This cannot be undone.
            </p>
            {declineError && <div className="form-error">{declineError}</div>}
            <Input
              label="Reason for the client (optional)"
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. No trucks available on that date"
            />
          </>
        }
        confirmLabel="Decline trip"
        cancelLabel="Keep trip"
        danger
        loading={loading}
      />

      <AssignmentModal
        open={assignTripId !== null}
        onClose={() => setAssignTripId(null)}
        onSubmit={handleApprove}
        loading={loading}
        submitError={assignError}
        trip={
          assignTrip
            ? { label: assignTrip.tripNumber ?? assignTrip.reference, clientName: assignTrip.clientName, vehicleTypeId: assignTrip.vehicleTypeId ?? null }
            : null
        }
      />

      <style>{`
        .approval-tabs {
          display: flex;
          gap: var(--space-sm);
          margin-bottom: var(--space-lg);
          border-bottom: 1px solid var(--color-border);
          flex-wrap: wrap;
        }
        .approval-tab {
          background: none;
          border: none;
          padding: var(--space-sm) var(--space-md);
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--color-text-muted);
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }
        .approval-tab.active {
          color: var(--color-forest);
          border-bottom-color: var(--color-forest);
        }
      `}</style>
    </Layout>
  );
}

function ExceptionApprovalTab({
  exceptions,
  loading,
  loadError,
  onRetry,
  onDecided,
}: {
  exceptions: ExceptionRecord[];
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  onDecided: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [acceptError, setAcceptError] = useState('');
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<ExceptionRecord | null>(null);
  const [overrideDecision, setOverrideDecision] = useState<'approve' | 'waive' | 'apply'>('approve');
  const [overrideReason, setOverrideReason] = useState('');
  const [overridePeso, setOverridePeso] = useState('');
  const [overrideError, setOverrideError] = useState('');
  const [saving, setSaving] = useState(false);

  const acceptDefault = async (ex: ExceptionRecord) => {
    setAcceptingId(ex.id);
    setAcceptError('');
    try {
      await exceptionsApi.decide(ex.id, {
        decision: 'approve',
        decisionReason: `Accepted default: ${BILLABILITY_LABEL[ex.defaultBillability ?? ''] ?? 'no default'}${ex.recommendedAction ? ` — ${ex.recommendedAction}` : ''}`,
      });
      toast(`${humanize(ex.exceptionType)} decided`);
      onDecided();
    } catch (err) {
      setAcceptError((err as { message?: string }).message ?? 'Failed to record decision');
    } finally {
      setAcceptingId(null);
    }
  };

  const openOverride = (ex: ExceptionRecord) => {
    setOverrideTarget(ex);
    setOverrideDecision('approve');
    setOverrideReason('');
    setOverridePeso('');
    setOverrideError('');
  };

  const submitOverride = async () => {
    if (!overrideTarget || !overrideReason.trim()) {
      setOverrideError('A reason is required to override the default');
      return;
    }
    setSaving(true);
    setOverrideError('');
    try {
      await exceptionsApi.decide(overrideTarget.id, {
        decision: overrideDecision,
        decisionReason: overrideReason,
        pesoEffect: overridePeso || undefined,
      });
      setOverrideTarget(null);
      toast('Exception decision saved');
      onDecided();
    } catch (err) {
      setOverrideError((err as { message?: string }).message ?? 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  if (loadError) {
    return (
      <div className="empty-state">
        <h3>Could not load exceptions</h3>
        <p>{loadError}</p>
        <Button type="button" onClick={onRetry}>Retry</Button>
      </div>
    );
  }

  if (exceptions.length === 0) {
    return (
      <div className="empty-state">
        <h3>No exceptions pending decision</h3>
        <p>Field-filed exceptions appear here for billability decisions. Decided exceptions leave this queue.</p>
      </div>
    );
  }

  return (
    <>
      {acceptError && <div className="form-error">{acceptError}</div>}
      <div className="exception-list">
        {exceptions.map((ex) => {
          const age = ageInDays(ex.filedAt ?? ex.createdAt);
          return (
            <div key={ex.id} className={`exception-card ${age >= 3 ? 'aged' : ''}`}>
              <div className="exception-main">
                <div className="row" style={{ gap: 'var(--space-sm)', alignItems: 'center' }}>
                  <strong>{humanize(ex.exceptionType)}</strong>
                  {ex.defaultBillability && (
                    <span className={`billability-badge billability-${ex.defaultBillability}`}>
                      {BILLABILITY_LABEL[ex.defaultBillability]}
                    </span>
                  )}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate(`/app/dispatch/trips/${ex.tripId}`)}
                  >
                    View trip →
                  </button>
                </div>
                <p className="text-muted" style={{ fontSize: '0.875rem', margin: 'var(--space-xs) 0' }}>
                  {humanize(ex.reasonCode)}
                  {ex.recommendedAction ? ` · Recommended: ${ex.recommendedAction}` : ''}
                </p>
                <span className={`text-muted age-tag ${age >= 3 ? 'text-urgent' : ''}`} style={{ fontSize: '0.75rem' }}>
                  Filed {age}d ago{ex.causeSide ? ` · Cause: ${humanize(ex.causeSide)}` : ''}
                </span>
              </div>
              <div className="exception-actions">
                <Button
                  type="button"
                  size="sm"
                  loading={acceptingId === ex.id}
                  onClick={() => acceptDefault(ex)}
                  disabled={!ex.defaultBillability}
                >
                  Accept default
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => openOverride(ex)}>
                  Override
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={overrideTarget !== null}
        onClose={() => setOverrideTarget(null)}
        title="Override exception decision"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOverrideTarget(null)}>Cancel</Button>
            <Button onClick={submitOverride} loading={saving}>Save decision</Button>
          </>
        }
      >
        {overrideError && <div className="form-error">{overrideError}</div>}
        <Select
          label="Decision"
          value={overrideDecision}
          onChange={(e) => setOverrideDecision(e.target.value as typeof overrideDecision)}
        >
          <option value="approve">Approve (as billable)</option>
          <option value="waive">Waive (non-billable)</option>
          <option value="apply">Apply charge</option>
        </Select>
        <Input
          label="Charge adjustment (₱, negative = credit)"
          type="number"
          inputMode="decimal"
          step="0.01"
          hint="Optional."
          value={overridePeso}
          onChange={(e) => setOverridePeso(e.target.value)}
          placeholder="0.00"
        />
        <Input
          label="Reason (required)"
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="Explain the override…"
          required
        />
      </Modal>

      <style>{`
        .exception-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }
        .exception-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-md) var(--space-lg);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-md);
        }
        .exception-card.aged { border-left: 3px solid var(--color-warning); }
        .exception-main { flex: 1; min-width: 0; }
        .exception-actions {
          display: flex;
          gap: var(--space-sm);
          flex-shrink: 0;
        }
        .billability-badge {
          font-size: 0.6875rem;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 999px;
          text-transform: uppercase;
        }
        .billability-billable { background: #E3F3E6; color: var(--color-forest); }
        .billability-non_billable { background: var(--color-fog); color: var(--color-text-muted); }
        .billability-case_by_case { background: #FDF3E7; color: var(--color-warning); }
        .link-button {
          background: none;
          border: none;
          color: var(--color-forest);
          cursor: pointer;
          font-size: 0.8125rem;
          font-weight: 600;
          padding: 0;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
      `}</style>
    </>
  );
}
